import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { api, fmt } from "../client";
import { Pill, TierBadge } from "../ui";
import UserActionDialog from "./UserActionDialog";
import { useStaff } from "../StaffAuth";

const PURGE_MSG = "Permanently purge this account and all linked records immediately? This cannot be undone.";

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-2 text-sm">
    <span className="text-[var(--s-dim)]">{k}</span>
    <span className="s-mono break-all text-right" data-testid={testid}>{v ?? "—"}</span>
  </div>
);

const Section = ({ title, children }) => (
  <section className="border-t border-[var(--s-line)] py-5">
    <h3 className="s-mono mb-3 text-[11px] uppercase tracking-[0.2em] text-[var(--s-accent)]">{title}</h3>
    {children}
  </section>
);

export default function UserDrawer({ uid, onClose, onChanged }) {
  const [u, setU] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { staff } = useStaff();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const lifecycle = async (path, msg, confirms, body) => {
    if (busy || !confirms.every((c) => window.confirm(c))) return;
    setBusy(true);
    try {
      await api(`/staff/users/${uid}/${path}`, { method: "POST", body });
      toast.success(msg);
      if (path === "purge") { onChanged(); onClose(); } else { load(); onChanged(); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const startPurge = async () => {
    if (busy) return;
    let c;
    setBusy(true);
    try { c = await api(`/staff/users/${uid}/purge-preview`); } catch (e) { toast.error(e.message); return; } finally { setBusy(false); }
    setPreview(c);
    const summary = `\n\nThis will delete:\n- ${c.matches} matches\n- ${c.messages} chat messages\n- ${c.swipes} swipes\n- ${c.files} storage files (${c.photos} photo records)\n- ${c.has_login ? "the Supabase login" : "no Supabase login found"}`;
    if (!window.confirm(PURGE_MSG + summary)) return;
    const expected = u.email || u.user_id;
    const typed = window.prompt(`Type ${u.email ? "the user's email" : "the account ID"} to confirm the purge:\n${expected}`, "");
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== expected.toLowerCase()) {
      toast.error("Confirmation did not match. Nothing was deleted.");
      return;
    }
    await lifecycle("purge", "Account purged", [], { confirm: typed.trim() });
  };
  const load = useCallback(() => {
    if (uid) api(`/staff/users/${uid}`).then(setU).catch((e) => toast.error(e.message));
  }, [uid]);
  useEffect(() => { setU(null); setPreview(null); load(); }, [load]);
  const quick = async (path, msg) => {
    try { await api(`/staff/users/${uid}/${path}`, { method: "POST" }); toast.success(msg); load(); }
    catch (e) { toast.error(e.message); }
  };
  const btn = (label, onClick, testid, cls = "s-ghost") => (
    <Button size="sm" variant="outline" className={cls} onClick={onClick} data-testid={testid}>{label}</Button>
  );
  return (
    <Sheet open={!!uid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="staff-root w-full overflow-y-auto border-[var(--s-line)] bg-[var(--s-panel)] sm:max-w-lg" data-testid="user-drawer">
        {!u ? <><SheetTitle className="sr-only">Account details</SheetTitle><p className="p-4 text-sm text-[var(--s-dim)]">Loading…</p></> : (
          <>
            <SheetHeader className="pb-5 text-left">
              <SheetTitle className="s-display text-2xl text-[var(--s-text)]" data-testid="user-drawer-name">{u.display_name || "Unnamed user"}</SheetTitle>
              <div className="flex flex-wrap gap-2"><TierBadge tier={u.tier} data-testid="user-drawer-tier" />{u.banned && <Pill tone="bad">Banned</Pill>}{u.suspended_until && new Date(u.suspended_until) > new Date() && <Pill tone="warn">Suspended until {fmt(u.suspended_until)}</Pill>}</div>
            </SheetHeader>
            <Section title="Identity verification">
              <div className="rounded-lg border border-[var(--s-line)] p-3" data-testid="user-drawer-idv-card">
                <div className="mb-1"><Pill tone={u.is_id_verified ? "ok" : "dim"} data-testid="user-drawer-idv-status">{u.is_id_verified ? "ID Verified" : "Not verified"}</Pill></div>
                <Row k="Legal Name" v={[u.legal_first_name, u.legal_last_name].filter(Boolean).join(" ") || "—"} testid="user-drawer-legal-name" />
                <Row k="Verified Age" v={u.is_id_verified ? u.age ?? "—" : "—"} testid="user-drawer-verified-age" />
              </div>
            </Section>
            <Section title="Account & device">
              <Row k="Account ID" v={u.user_id} testid="user-drawer-id" />
              <Row k="Email" v={u.email} testid="user-drawer-email" />
              <Row k="Phone" v={u.phone} testid="user-drawer-phone" />
              <Row k="Didit verified" v={u.id_verified ? "Yes" : "No"} testid="user-drawer-didit" />
              <Row k="Age / DOB" v={`${u.age ?? "—"} / ${u.dob ?? "—"}`} testid="user-drawer-age" />
              <Row k="Hardware ID" v={u.hardware_id || "Not recorded"} testid="user-drawer-hardware" />
            </Section>
            <Section title="Consumables">
              <div className="grid grid-cols-5 gap-2" data-testid="user-drawer-consumables">
                {Object.entries(u.consumables).map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-[var(--s-line)] p-2 text-center">
                    <p className="s-display text-xl" data-testid={`user-consumable-${k}`}>{v}</p>
                    <p className="s-mono text-[9px] uppercase text-[var(--s-dim)]">{k}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3">{btn("Grant consumables", () => setDialog("grant"), "user-grant-btn")}</div>
            </Section>
            <Section title="Support operations">
              <div className="flex flex-wrap gap-2">
                {btn("Send password reset email", () => quick("password-reset-email", "Reset email sent"), "user-reset-email-btn")}
                {btn("Update contact info", () => setDialog("contact"), "user-contact-btn")}
                {btn("Force sign-out", () => quick("force-signout", "Sessions dropped"), "user-signout-btn")}
              </div>
            </Section>
            <Section title="Safety & moderation">
              <div className="flex flex-wrap gap-2">
                {btn("Issue warning", () => setDialog("warning"), "user-warning-btn")}
                {btn("Suspend", () => setDialog("suspend"), "user-suspend-btn", "s-warn")}
                {!u.banned && btn("Permanent ban", () => setDialog("ban"), "user-ban-btn", "s-danger")}
              </div>
            </Section>
            {staff?.role === "super_admin" && (
              <Section title="Super admin lifecycle">
                <div className="flex flex-wrap gap-2" data-testid="user-lifecycle-actions">
                  {(u.banned || u.deleted) && btn("Restore account", () => lifecycle("restore", "Account restored", ["Restore this account and lift the ban?"]), "user-restore-btn")}
                  {btn("Reset to onboarding", () => lifecycle("reset-onboarding", "Profile reset to onboarding", ["Wipe this profile, matches, chats and swipes and send the user back to onboarding?"]), "user-reset-onboarding-btn", "s-warn")}
                  <Button size="sm" disabled={busy} className="s-btn-danger" onClick={startPurge} data-testid="user-purge-btn">Instant delete (purge)</Button>
                </div>
                {preview && (
                  <p className="s-mono mt-3 text-xs text-red-300" data-testid="user-purge-preview">
                    Will delete {preview.matches} matches · {preview.messages} messages · {preview.swipes} swipes · {preview.files} files
                  </p>
                )}
              </Section>
            )}
            <Section title="Staff history">
              {u.history.length ? u.history.map((h) => (
                <p key={h.id} className="py-1 text-xs"><span className="s-mono text-[var(--s-accent)]">{h.action_type}</span> <span className="text-[var(--s-dim)]">· {h.staff_email} · {fmt(h.created_at)}</span></p>
              )) : <p className="text-xs text-[var(--s-dim)]">No staff actions yet.</p>}
            </Section>
          </>
        )}
        <UserActionDialog kind={dialog} user={u} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); onChanged(); }} />
      </SheetContent>
    </Sheet>
  );
}
